import React from 'react';
import { GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { alpha, Box, Chip, Skeleton, styled, Tooltip, Typography } from '@mui/material';
import AdsIcon from '@mui/icons-material/AdsClick';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/LockOutline';
import { useCubeQuery } from '@cubejs-client/react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import { StatusCell } from 'ui/table/StatusCell';
import logger from 'lib/logger';
import {
    ITrackingLink,
    TRACKING_LINK_CLICKS_MEASURE,
    TRACKING_LINK_SHORT_LINK_ID_DIMENSION,
    TrackingLinkType,
    TrackingLinkTypeLabels
} from '../trackingLinks';
import { useAppSelector } from 'store/hooks';
import { DEFAULT_PAGE_LIMIT } from 'lib/hooks/usePagedData';
import { QueryParameter } from 'lib/HttpClient';
import { copyToClipboard, updateArrayItem } from 'lib/helpers';
import { Option } from 'lib/types';
import { linksApi } from '../trackingLinksApi';
import { isDefined } from 'lib/typeguards';
import { buildShortLink } from 'components/tracking-links/helpers';
import { CAMPAIGNS_TRACKING_LINKS_VIEW } from 'config/routes';

export type LinkStatus = 'active' | 'archived';

export const linkStatusOptions: Option[] = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Archived', value: 'archived' }
];

const LockedChip = styled(Chip)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.grey[500], 0.1),
    borderColor: alpha(theme.palette.grey[500], 0.25),
    color: theme.palette.grey[700],
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: '0.75rem',
    '.MuiChip-icon': {
        fontSize: '0.75rem',
        color: theme.palette.grey[600]
    }
}));

const renderIsArchivedCell = (params: GridRenderCellParams) => (
    <StatusCell
        value={params.value ? 'false' : 'true'}
        labelMap={{ true: 'Active', false: 'Archived' }}
        colorMap={{ true: 'green', false: 'red' }}
        defaultColor="grey"
    />
);

const renderLockedCell = () => (
    <LockedChip
        icon={<LockIcon sx={{ fontSize: '0.625rem' }} />}
        size="small"
        label="Premium"
        variant="outlined"
    />
);

const renderTypeCell = (params: GridRenderCellParams) => {
    const type = params.value as TrackingLinkType;
    return (
        <StatusCell
            value={type}
            labelMap={{
                [TrackingLinkType.APP_LINK]: TrackingLinkTypeLabels[TrackingLinkType.APP_LINK],
                [TrackingLinkType.EXTERNAL_LINK]: TrackingLinkTypeLabels[TrackingLinkType.EXTERNAL_LINK]
            }}
            colorMap={{ [TrackingLinkType.APP_LINK]: 'purple', [TrackingLinkType.EXTERNAL_LINK]: 'yellow' }}
            defaultColor="yellow"
        />
    );
};

const renderClicksHeader = () => (
    <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="body2" fontWeight={500} fontSize="0.875rem">
            Clicks
        </Typography>
        <Tooltip
            title="Click stats can take up to 2 hours to appear - check back soon if you're not seeing any yet."
            placement="top"
            arrow
        >
            <InfoOutlinedIcon sx={{ fontSize: '0.875rem', color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
    </Box>
);

const renderClicksCell = (params: GridRenderCellParams) => {
    const isClicksLoading = params.row.isClicksLoading;
    return (
        <Box display="flex" alignItems="center" gap={1} height="100%">
            <AdsIcon sx={{ fontSize: '1rem' }} color="action" />
            {isClicksLoading ? (
                <Skeleton variant="text" width={80} height={24} />
            ) : (
                <Typography variant="body2">{params.value ?? 0}</Typography>
            )}
        </Box>
    );
};

const formatValue = (alternative: string) => (value: string | undefined) => value || alternative;

export function useTrackingLinksTableData() {
    const { settings } = useAppSelector(state => state.settings);
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const utmParametersEnabled = React.useMemo(
        () => settings?.links?.utmParametersEnabled ?? false,
        [settings?.links?.utmParametersEnabled]
    );
    const [links, setLinks] = React.useState<ITrackingLink[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState<GridRowSelectionModel>();
    const [endOfData, setEndOfData] = React.useState(false);
    const [page, setPage] = React.useState(0);
    const [pages, setPages] = React.useState([undefined]);
    const [status, setStatus] = React.useState<LinkStatus>('active');
    const [createLinkModalOpen, setCreateLinkModalOpen] = React.useState(false);
    const [downloadQRModalOpen, setDownloadQRModalOpen] = React.useState(false);
    const [archiveDialogOpen, setArchiveDialogOpen] = React.useState(false);
    const [archiveLoading, setArchiveLoading] = React.useState(false);
    const [activateDialogOpen, setActivateDialogOpen] = React.useState(false);
    const [activateLoading, setActivateLoading] = React.useState(false);

    const rowsPerPage = DEFAULT_PAGE_LIMIT;

    React.useEffect(() => {
        const getLinks = async () => {
            if (endOfData) {
                return;
            }
            setLoading(true);
            try {
                const queryParameters: QueryParameter[] = [{ key: 'limit', value: rowsPerPage }];
                if (pages[page]) {
                    queryParameters.push({ key: 'startKey', value: pages[page] });
                }
                if (status === 'archived') {
                    queryParameters.push({ key: 'archived', value: true });
                }
                if (status === 'active') {
                    queryParameters.push({ key: 'archived', value: false });
                }
                const { body, ok } = await linksApi.getList({
                    queryParameters
                });
                if (!ok) {
                    throw new Error(body.message);
                }
                if (!body.page.nextKey) {
                    setEndOfData(true);
                }
                setLinks(currentLinks => [
                    ...currentLinks,
                    ...body.items.map((item: ITrackingLink) => ({
                        ...item,
                        id: item.shortLinkId
                    }))
                ]);
                setPages(currentPages => updateArrayItem(currentPages, page + 1, body.page.nextKey));
            } catch (error) {
                logger.error(error);
                setLinks([]);
            } finally {
                setLoading(false);
            }
        };
        if (links.length <= page * rowsPerPage && !endOfData) {
            getLinks();
        }
    }, [endOfData, links.length, page, pages, rowsPerPage, status]);

    const columns = React.useMemo<GridColDef[]>(
        () => [
            {
                field: 'internalTitle',
                headerName: 'Link Name',
                width: 250,
                sortable: false
            },
            {
                field: 'isArchived',
                headerName: 'Status',
                width: 100,
                sortable: false,
                renderCell: renderIsArchivedCell
            },
            {
                field: 'utmSource',
                headerName: 'Source',
                width: 100,
                sortable: false,
                renderCell: utmParametersEnabled ? undefined : renderLockedCell,
                valueFormatter: formatValue('-')
            },
            {
                field: 'utmMedium',
                headerName: 'Medium',
                width: 100,
                sortable: false,
                renderCell: utmParametersEnabled ? undefined : renderLockedCell,
                valueFormatter: formatValue('-')
            },
            {
                field: 'utmCampaign',
                headerName: 'Campaign (UTM)',
                width: 200,
                sortable: false,
                renderCell: utmParametersEnabled ? undefined : renderLockedCell,
                valueFormatter: formatValue('-')
            },
            {
                field: 'type',
                headerName: 'Type',
                width: 130,
                sortable: false,
                renderCell: renderTypeCell,
                valueFormatter: formatValue('-')
            },
            {
                field: 'clicks',
                headerName: 'Clicks',
                width: 120,
                sortable: false,
                renderHeader: renderClicksHeader,
                renderCell: renderClicksCell
            },
            { field: 'createdAt', headerName: 'Created', width: 150 }
        ],
        [utmParametersEnabled]
    );

    const handleSelectRow = React.useCallback((newSelectedRow: GridRowSelectionModel) => {
        if (newSelectedRow.length > 1) {
            setSelectedRow([newSelectedRow[newSelectedRow.length - 1]]);
        } else {
            setSelectedRow(newSelectedRow);
        }
    }, []);

    const onPageChange = React.useCallback(
        (_: unknown, newPage: number) => {
            if (!loading) {
                setPage(newPage);
            }
        },
        [loading]
    );

    const refetch = React.useCallback(() => {
        setLinks([]);
        setPages([undefined]);
        setPage(0);
        setEndOfData(false);
    }, []);

    const handleStatusChange = React.useCallback((newStatus: LinkStatus) => {
        setStatus(newStatus);
        setLinks([]);
        setPages([undefined]);
        setPage(0);
        setEndOfData(false);
        setSelectedRow([]);
    }, []);

    const count = React.useMemo(() => {
        if (endOfData) {
            return links.length;
        }
        return -1;
    }, [endOfData, links.length]);

    const pagedLinks = React.useMemo(() => {
        if (!Array.isArray(links)) {
            return [];
        }
        return links.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [links, page, rowsPerPage]);

    const pagedShortLinkIds = React.useMemo(() => pagedLinks.map(link => link.shortLinkId), [pagedLinks]);

    const clicksQuery = useCubeQuery(
        {
            measures: [TRACKING_LINK_CLICKS_MEASURE],
            dimensions: [TRACKING_LINK_SHORT_LINK_ID_DIMENSION],
            filters: [
                {
                    member: TRACKING_LINK_SHORT_LINK_ID_DIMENSION,
                    operator: 'equals',
                    values: pagedShortLinkIds
                }
            ]
        },
        { skip: pagedShortLinkIds.length === 0 }
    );

    const clicksByShortLinkId = React.useMemo(() => {
        const rows = clicksQuery.resultSet?.tablePivot() ?? [];
        return rows.reduce<Record<string, number>>((acc, row) => {
            const id = row[TRACKING_LINK_SHORT_LINK_ID_DIMENSION] as string;
            acc[id] = Number(row[TRACKING_LINK_CLICKS_MEASURE] ?? 0);
            return acc;
        }, {});
    }, [clicksQuery.resultSet]);

    const pagedLinksWithClicks = React.useMemo(
        () =>
            pagedLinks.map(link => ({
                ...link,
                clicks: clicksByShortLinkId[link.shortLinkId] ?? 0,
                isClicksLoading: clicksQuery.isLoading
            })),
        [pagedLinks, clicksByShortLinkId, clicksQuery.isLoading]
    );

    const isLinkSelected = React.useMemo(() => selectedRow?.length > 0, [selectedRow]);

    const selectedLink = React.useMemo(
        () => links.find(link => link.shortLinkId === selectedRow?.[0]),
        [links, selectedRow]
    );

    const isSelectedLinkValid = React.useMemo(
        () => isDefined(selectedLink) && !selectedLink.isArchived,
        [selectedLink]
    );

    const isSelectedLinkArchived = React.useMemo(
        () => isDefined(selectedLink) && selectedLink.isArchived,
        [selectedLink]
    );

    const selectedShortLinkUrl = React.useMemo(
        () =>
            selectedLink
                ? buildShortLink(import.meta.env.TRACKING_SHORT_LINK_URL, selectedLink.shortLinkId)
                : '',
        [selectedLink]
    );

    const handleOpenCreateLinkModal = React.useCallback(() => {
        setCreateLinkModalOpen(true);
    }, []);

    const handleCloseCreateLinkModal = React.useCallback(() => {
        setCreateLinkModalOpen(false);
    }, []);

    const handleCreateLinkSubmit = React.useCallback(() => {
        setCreateLinkModalOpen(false);
        refetch();
    }, [refetch]);

    const handleViewLink = React.useCallback(() => {
        navigate(CAMPAIGNS_TRACKING_LINKS_VIEW.replace(':linkId', selectedLink.shortLinkId));
    }, [navigate, selectedLink]);

    const handleDownloadQR = React.useCallback(() => {
        setDownloadQRModalOpen(true);
    }, []);

    const handleCopyLink = React.useCallback(() => {
        if (!isSelectedLinkValid || !selectedShortLinkUrl) {
            return;
        }
        copyToClipboard(selectedShortLinkUrl);
        enqueueSnackbar('Link copied to clipboard', { variant: 'info' });
    }, [isSelectedLinkValid, selectedShortLinkUrl, enqueueSnackbar]);

    const handleArchiveLink = React.useCallback(() => {
        setArchiveDialogOpen(true);
    }, []);

    const handleCloseArchiveDialog = React.useCallback(() => {
        setArchiveDialogOpen(false);
    }, []);

    const handleConfirmArchive = React.useCallback(async () => {
        if (!selectedLink) {
            return;
        }
        setArchiveLoading(true);
        try {
            const { ok, body } = await linksApi.update(selectedLink.shortLinkId, {
                body: { isArchived: true }
            });
            if (!ok) {
                throw new Error(body?.message);
            }
            enqueueSnackbar('Link archived', { variant: 'success' });
            setArchiveDialogOpen(false);
            handleSelectRow([]);
            refetch();
        } catch (error) {
            logger.error(error);
            enqueueSnackbar('Failed to archive link', { variant: 'error' });
        } finally {
            setArchiveLoading(false);
        }
    }, [selectedLink, enqueueSnackbar, handleSelectRow, refetch]);

    const handleActivateLink = React.useCallback(() => {
        setActivateDialogOpen(true);
    }, []);

    const handleCloseActivateDialog = React.useCallback(() => {
        setActivateDialogOpen(false);
    }, []);

    const handleConfirmActivate = React.useCallback(async () => {
        if (!selectedLink) {
            return;
        }
        setActivateLoading(true);
        try {
            const { ok, body } = await linksApi.update(selectedLink.shortLinkId, {
                body: { isArchived: false }
            });
            if (!ok) {
                throw new Error(body?.message);
            }
            enqueueSnackbar('Link activated', { variant: 'success' });
            setActivateDialogOpen(false);
            handleSelectRow([]);
            refetch();
        } catch (error) {
            logger.error(error);
            enqueueSnackbar('Failed to activate link', { variant: 'error' });
        } finally {
            setActivateLoading(false);
        }
    }, [selectedLink, enqueueSnackbar, handleSelectRow, refetch]);

    const handleCloseDownloadQRModal = React.useCallback(() => {
        setDownloadQRModalOpen(false);
        handleSelectRow([]);
    }, [handleSelectRow]);

    return {
        columns,
        links,
        pagedLinks: pagedLinksWithClicks,
        loading,
        selectedRow,
        handleSelectRow,
        page,
        onPageChange,
        count,
        rowsPerPage,
        endOfData,
        refetch,
        status,
        handleStatusChange,
        isLinkSelected,
        selectedLink,
        isSelectedLinkValid,
        isSelectedLinkArchived,
        selectedShortLinkUrl,
        createLinkModalOpen,
        downloadQRModalOpen,
        archiveDialogOpen,
        archiveLoading,
        activateDialogOpen,
        activateLoading,
        handleOpenCreateLinkModal,
        handleCloseCreateLinkModal,
        handleCreateLinkSubmit,
        handleViewLink,
        handleDownloadQR,
        handleCopyLink,
        handleArchiveLink,
        handleCloseArchiveDialog,
        handleConfirmArchive,
        handleActivateLink,
        handleCloseActivateDialog,
        handleConfirmActivate,
        handleCloseDownloadQRModal
    };
}
